/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Jan 30 2024
 **/

import { useEffect, useRef } from "react";

import { EventHandler } from "@rfice/eventhandler";

import { Button, Row, Typography } from "@/frameworks/designStuff/components";
import { Utility } from "@/frameworks/libraries/utilities";
import useDocumentViewerStore from "../../stores/useDocumentViewerStore";
import { Input } from "./emotion";
import { MAX_SCALE } from "../../viewer/constant";

export default function InputRange({ minimize }: { minimize: boolean }) {
	const inputRef = useRef<HTMLInputElement>(null);

	const scale = useDocumentViewerStore((state) => state.scale);
	const setScale = useDocumentViewerStore((state) => state.setScale);
	const minScale = useDocumentViewerStore((state) => state.minScale);
	const resetScale = useDocumentViewerStore((state) => state.resetScale);

	const steps = [25, 50, 75, 100, 125, 150, 175, 200];
	const stepsThreshold = 5;
	const defaultValue = 100;

	// scale, minScale to init value
	useEffect(() => {
		return () => resetScale();
	}, []);

	// on minScale initialized, set input min to minScale
	useEffect(() => {
		if (inputRef.current) inputRef.current.min = String(minScale * 100);
	}, [minScale]);

	// change from viewer
	useEffect(() => {
		if (inputRef.current) inputRef.current.value = String(scale * 100);
	}, [scale]);

	// change from slider
	const handleChangeValue = (e: React.ChangeEvent<HTMLInputElement>) => {
		// slider onChange에서 event fire를 하지 않음, stomp issue
		setScale(Number(e.target.value) / 100);
	};

	const onDoneChangeSlider = () => {
		// slider on done change에서 event fire
		EventHandler.fire("$InputRange::ScaleUpdated$", scale);
	};

	// change from shortcut
	useEffect(() => {
		const keyDownEvent = (ev: KeyboardEvent) => {
			if (ev.key === "=") onClickZoomButton("up");
			if (ev.key === "-") onClickZoomButton("down");
		};
		window.addEventListener("keydown", keyDownEvent);
		return () => window.removeEventListener("keydown", keyDownEvent);
	}, [scale]);

	// change from zoom button
	const onClickZoomButton = (type: "down" | "up") => {
		if (inputRef.current) {
			// get value
			const value = getNextStepValue(type === "down" ? -1 : 1);
			// set input
			inputRef.current.value = value;
			// set scale
			const targetScale = Number(value) / 100;
			setScale(targetScale);
			// fire event
			EventHandler.fire("$InputRange::ScaleUpdated$", targetScale);
		}
	};

	// get next value logic
	const getNextStepValue = (inc: 1 | -1) => {
		const clamp = (value: number) => Math.max(0, Math.min(value, steps.length - 1));
		const currentValue = scale * 100;
		const targetMinScale = minScale * 100;
		const closestStepIndex = steps.findLastIndex((step) => step <= currentValue);
		let targetIndex = clamp(closestStepIndex + (inc === 1 ? 1 : 0));
		const diff = Math.abs(steps[targetIndex] - currentValue);
		if (diff <= stepsThreshold) targetIndex = clamp(targetIndex + inc);

		if (steps[targetIndex] < targetMinScale) return String(targetMinScale);
		return String(steps[targetIndex]);
	};

	return (
		<Row verticalAlign="center" css={{ visibility: minimize ? "hidden" : "visible" }}>
			<Button.Icon
				size="large"
				name="Minus24"
				fill="neutral_00_100"
				filled="transparent"
				autoFocus
				onClick={() => onClickZoomButton("down")}
			/>

			<Input
				ref={inputRef}
				type="range"
				min={String(minScale * 100)}
				max={String(MAX_SCALE * 100)}
				id="myRange"
				step="1"
				defaultValue={defaultValue}
				onChange={handleChangeValue}
				css={{
					width: Utility.isMobile ? "90px" : "220px",
					margin: Utility.isMobile ? "0 5px" : "0 10px"
				}}
				onKeyDown={(e) => e.preventDefault()}
				onMouseUp={onDoneChangeSlider}
			/>
			<Button.Icon
				size="large"
				name="Add24"
				fill="neutral_00_100"
				filled="transparent"
				onClick={() => onClickZoomButton("up")}
			/>

			<Typography.div
				typoColor="neutral_00_100"
				typoSize="Body3"
				css={{
					width: "39px"
				}}
			>
				{Math.round(scale * 100)}%
			</Typography.div>
		</Row>
	);
}
